/**
 * Google Tag Manager — wstrzykuje kontener GTM po stronie klienta.
 *
 * ID kontenera czytane z runtimeConfig.public.gtmId (env NUXT_PUBLIC_GTM_ID),
 * więc inventory/swarm ustala je przy starcie kontenera — bez rebuildu obrazu.
 * Pusty ID => no-op (dev / środowiska bez ustawionej zmiennej nic nie ładują).
 *
 * Kolejność (`enforce: 'pre'`): plugin MUSI wystartować przed consent.client.ts,
 * bo to on tworzy `window.dataLayer` i wypycha Consent Mode v2 default = denied.
 * Dopiero wtedy istniejące `gtag('consent','update')` z banera (useAnalyticsConsent
 * → useGtag, też push do window.dataLayer) trafiają do WSPÓLNEGO dataLayer i są
 * odczytywane przez GTM. Bez tego (nuxt-gtag bez ID nie tworzy dataLayer) zgody
 * byłyby cichym no-opem. `wait_for_update: 500` daje kontenerowi okno na
 * zaaplikowanie zapisanej zgody, zanim odpali tagi consent-gated.
 *
 * CSP: pod 'strict-dynamic' skrypt wstrzyknięty przez zaufany (nonce) runtime
 * Nuxt jest autoryzowany — bez ręcznego nonce. www.googletagmanager.com jest już
 * w script-src/connect-src (nuxt.config → security). Pomijamy <noscript>-iframe
 * GTM (sklep wymaga JS), więc frame-src pozostaje bez zmian.
 *
 * Wydajność (CWV): samo ŁADOWANIE gtm.js jest odroczone do PIERWSZEJ INTERAKCJI
 * użytkownika (onFirstInteraction: scroll/klik/dotyk/klawisz, + fallback timeout),
 * bo kontener ciągnie Clarity/TikTok/Meta/Ads — najcięższy pojedynczy podatek na
 * wątek główny (TBT/INP) tuż po starcie. Wcześniej było onNuxtReady (idle po
 * hydracji), ale to odpala się W OKNIE pomiaru Lighthouse → third-party nadal
 * liczył się do TBT. Interakcja: Lighthouse/boty nie klikają, więc kontener nie
 * odpala się w oknie CWV, a realny user (który niemal zawsze scrolluje) dostaje
 * go od razu. Tworzenie dataLayer i Consent Mode default ZOSTAJE synchroniczne
 * (`enforce: 'pre'`, przed consent.client.ts) — zdarzenia i zgody kolejkują się
 * w dataLayer, a GTM przetworzy je po doładowaniu. Kliknięcie „Akceptuj" w banerze
 * zgody jest interakcją, więc kontener ładuje się dokładnie wtedy, gdy zgoda pada.
 */
export default defineNuxtPlugin({
  name: 'gtm',
  enforce: 'pre',
  setup() {
    const gtmId = useRuntimeConfig().public.gtmId
    if (!gtmId) return

    const w = window as Window & { dataLayer?: unknown[] }
    w.dataLayer = w.dataLayer || []

    // gtag wypycha `arguments` — format komend rozpoznawany przez Consent Mode,
    // identycznie jak nuxt-gtag (window.dataLayer.push(arguments)).
    function gtag() {
      // eslint-disable-next-line prefer-rest-params
      w.dataLayer!.push(arguments)
    }

    // Consent Mode v2 — domyślnie wszystko denied. Klucze 1:1 z
    // useAnalyticsConsent.setConsent() oraz nuxt.config gtag.initCommands.
    // MUSI być synchronicznie przed consent.client.ts i przed gtm.js.
    gtag('consent', 'default', {
      ad_user_data: 'denied',
      ad_personalization: 'denied',
      ad_storage: 'denied',
      analytics_storage: 'denied',
      wait_for_update: 500,
    })

    // Ładowanie kontenera odroczone do pierwszej interakcji (onFirstInteraction),
    // BEZ fallbacku czasowego (`fallbackMs: null`) — timer potrafił odpalić się
    // jeszcze w oknie pomiaru Lighthouse na wolnym mobile → niestabilny wynik lab.
    // Bez timera trial (brak interakcji) jest deterministyczny; realny user dostaje
    // GTM przez interakcję lub przy opuszczaniu strony (visibilitychange w helperze).
    // gtm.start ustawiamy tuż przed wstrzyknięciem, by znacznik czasu odpowiadał
    // faktycznemu załadowaniu.
    onFirstInteraction(
      () => {
        w.dataLayer!.push({ 'gtm.start': Date.now(), event: 'gtm.js' })

        const script = document.createElement('script')
        script.async = true
        script.src = `https://www.googletagmanager.com/gtm.js?id=${gtmId}`
        document.head.appendChild(script)
      },
      { fallbackMs: null },
    )
  },
})
